import React, { useState, useEffect, Fragment, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Sentry from '@sentry/browser';
import Api from '../../../Api';
import FormatDate from '../../Utils/FormatDate';
import Status from './Status';
import { InputHelper, PasswordInputHelper } from '../../Utils/FieldsHelper';
import Modal from '../../Utils/Modal';
import Icon from '../../Utils/Icon';
import ProjectSearchList from '../ProjectSearchList';
import MessageContext from '../../../MessageContext';
import PermissionsEditor from '../PermissionsEditor';

import classes from './styles.module.css';

function AccountEditing({ data, token, onChangeAccountData, projectsList, isDataLoading }) {
    const [isLoading, setIsLoading] = useState(false);
    const [form, setForm] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(-1);
    const { onMessage } = useContext(MessageContext);
    const intl = useIntl();

    const [globalPermissionsEnabled, setGlobalPermissionsEnabled] = useState(false);
    const [globalPrivileges, setGlobalPrivileges] = useState(0);
    const [globalPermissionsModal, setGlobalPermissionsModal] = useState(false);
    const [initialGlobalEnabled, setInitialGlobalEnabled] = useState(false);

    const [permissionsModal, setPermissionsModal] = useState({
        open: false,
        project: null,
        accountId: null,
    });

    useEffect(() => {
        if (Object.keys(data).length) {
            setForm({
                status: data.status,
                username: data.username || '',
                password: '',
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                patronymic: data.patronymic || '',
                email: data.email || '',
                projects: data.projects || [],
            });
            setIsAdmin(data.privileges === 63 ? 1 : 0);

            const gpEnabled = data.globalPrivileges !== null && data.globalPrivileges !== undefined;
            setGlobalPermissionsEnabled(gpEnabled);
            setGlobalPrivileges(data.globalPrivileges != null ? data.globalPrivileges : 0);
            setInitialGlobalEnabled(gpEnabled);
        }
    }, [data]);

    function onChange(name, value) {
        setForm({ ...form, [name]: value });
    }

    function setCurrentStatus(id) {
        setForm({ ...form, status: id });
    }

    async function sendForm() {
        setIsLoading(true);
        setIsModalOpen(false);

        const api = new Api({ token });

        try {
            const payload = {
                ...form,
                projects: form.projects.map((p) => ({ id: p.id, uuid: p.uuid })),
            };

            const r = await api.setAccountData(data.id, payload);

            if (r && r.errors) {
                const errors = Object.values(r.errors).join(' ');
                onMessage({ type: 'error', message: `${r.message} ${errors}` });
                setIsLoading(false);
                return;
            }

            const currentIsAdmin = data.privileges === 63 ? 1 : 0;
            if (isAdmin !== currentIsAdmin) {
                const adminCall = isAdmin === 1 ? api.grantAdmin(data.id) : api.revokeAdmin(data.id);
                await adminCall;
            }

            if (globalPermissionsEnabled !== initialGlobalEnabled) {
                await api.updateGlobalPermissions(
                    data.id,
                    globalPermissionsEnabled,
                    globalPermissionsEnabled ? globalPrivileges : 0
                );
            }

            onChangeAccountData(payload, data.id);
            onMessage({
                type: 'success',
                message: intl.formatMessage({ id: 'AccountEditing.success.saved' }),
            });
            setForm({ ...form, password: '' });
        } catch (e) {
            Sentry.captureException(e);
            onMessage({
                type: 'error',
                message: intl.formatMessage({ id: 'AccountEditing.error.save' }),
            });
        } finally {
            setIsLoading(false);
        }
    }

    function onSaveAccountData() {
        if (form.password) {
            setIsModalOpen(true);
        } else {
            sendForm();
        }
    }

    function onEditPermissions(project, accountId) {
        setPermissionsModal({
            open: true,
            project: project,
            accountId: accountId,
        });
    }

    return (
        <Fragment>
            <Modal
                title={intl.formatMessage({ id: 'AccountEditing.status.passwordConfirm' }, { account: form.username })}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={sendForm}
                isConfirm
            >
                <FormattedMessage id="AccountEditing.status.passwordConfirm" values={{ account: form.username }} />
            </Modal>

            <div className={classes.Card}>
                {isDataLoading && (
                    <div className={classes.SavingOverlay}>
                        <div className={classes.SavingSpinner}></div>
                        <span>
                            <FormattedMessage id="AccountEditing.loading.data" />
                        </span>
                    </div>
                )}
                {isLoading && (
                    <div className={classes.SavingOverlay}>
                        <div className={classes.SavingSpinner}></div>
                        <span>
                            <FormattedMessage id="AccountEditing.loading.saving" />
                        </span>
                    </div>
                )}

                {Object.keys(data).length ? (
                    <Fragment>
                        <div className={classes.Content}>
                            <Status
                                currentStatus={form.status || data.status}
                                setCurrentStatus={setCurrentStatus}
                                isDisabled={isLoading}
                            />

                            <dl className={classes.List}>
                                <dt>
                                    <FormattedMessage id="AccountEditing.label.id" />
                                </dt>
                                <dd>{data.id}</dd>

                                <dt>
                                    <FormattedMessage id="AccountEditing.label.createdAt" />
                                </dt>
                                <dd>
                                    <FormatDate
                                        setDate={data.createdAt}
                                        format={{
                                            day: 'numeric',
                                            month: 'numeric',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: 'numeric',
                                        }}
                                    />
                                </dd>

                                <dt>
                                    <FormattedMessage id="AccountEditing.label.lastLoginAt" />
                                </dt>
                                <dd>
                                    {data.lastLoginAt ? (
                                        <FormatDate
                                            setDate={data.lastLoginAt}
                                            format={{
                                                day: 'numeric',
                                                month: 'numeric',
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: 'numeric',
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage id="AccountEditing.label.neverLoggedIn" />
                                    )}
                                </dd>

                                <dt>
                                    <FormattedMessage id="AccountEditing.label.updatedAt" />
                                </dt>
                                <dd>
                                    <FormatDate
                                        setDate={data.updatedAt}
                                        format={{
                                            day: 'numeric',
                                            month: 'numeric',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: 'numeric',
                                        }}
                                    />
                                </dd>
                            </dl>

                            <div className={classes.AdminBlock}>
                                <div className={classes.AdminCheckbox}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={isAdmin === 1}
                                            onChange={(e) => setIsAdmin(e.target.checked ? 1 : 0)}
                                            disabled={isLoading}
                                        />
                                        <span>
                                            <FormattedMessage id="AccountEditing.admin.label" />
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className={classes.GlobalPermissionsBlock}>
                                <div className={classes.GlobalPermissionsHeader}>
                                    <h4 className={classes.BlockTitle}>
                                        <FormattedMessage id="AccountEditing.global.title" />
                                    </h4>
                                    <span className={classes.GlobalPermissionsHint}>
                                        <FormattedMessage id="AccountEditing.global.hint" />
                                    </span>
                                </div>
                                <div className={classes.GlobalPermissionsActions}>
                                    <label className={classes.GlobalPermissionsToggle}>
                                        <input
                                            type="checkbox"
                                            checked={globalPermissionsEnabled}
                                            onChange={(e) => {
                                                setGlobalPermissionsEnabled(e.target.checked);
                                                if (!e.target.checked) {
                                                    setGlobalPrivileges(0);
                                                }
                                            }}
                                            disabled={isLoading}
                                        />
                                        <span>
                                            <FormattedMessage id="AccountEditing.global.enable" />
                                        </span>
                                    </label>
                                    <button
                                        className={classes.Btn}
                                        onClick={() => setGlobalPermissionsModal(true)}
                                        disabled={!globalPermissionsEnabled || isLoading}
                                    >
                                        <FormattedMessage id="AccountEditing.global.configure" />
                                    </button>
                                </div>
                            </div>

                            <div className={classes.Block}>
                                <div className={classes.Col_1}>
                                    <h4 className={classes.BlockTitle}>
                                        <FormattedMessage id="AccountEditing.title.account" />
                                    </h4>
                                </div>
                                <div className={classes.Col_2}>
                                    <InputHelper
                                        value={form.username}
                                        fieldName="username"
                                        label="FieldsHelper.label.username"
                                        isDisabled={true}
                                    />
                                </div>
                                <div className={classes.Col_3}>
                                    <PasswordInputHelper
                                        onChange={onChange}
                                        value={form.password}
                                        isDisabled={isLoading}
                                        label={intl.formatMessage({ id: 'FieldsHelper.password.generate' })}
                                    />
                                </div>
                            </div>

                            <div
                                className={classes.Btn}
                                onClick={() => {
                                    new Api({ token })
                                        .sendNewPassword(data.id)
                                        .then(() =>
                                            onMessage({
                                                type: 'success',
                                                message: intl.formatMessage({
                                                    id: 'AccountEditing.success.passwordSent',
                                                }),
                                            })
                                        )
                                        .catch((err) => {
                                            Sentry.captureException(err);
                                            onMessage({
                                                type: 'error',
                                                message: intl.formatMessage({
                                                    id: 'AccountEditing.error.sendPassword',
                                                }),
                                            });
                                        });
                                }}
                            >
                                <Icon icon="mail" size="16px" />{' '}
                                <FormattedMessage id="AccountEditing.btn.sendNewPassword" />
                            </div>

                            <div
                                className={classes.Btn}
                                onClick={() => {
                                    new Api({ token })
                                        .resetTwoFactor(data.id)
                                        .then((r) => {
                                            const msgId =
                                                r.message === '2FA has been reset for account'
                                                    ? 'AccountEditing.2FA.resetSuccess'
                                                    : r.message === '2FA was not enabled for account'
                                                      ? 'AccountEditing.2FA.notEnabled'
                                                      : null;
                                            onMessage({
                                                type: msgId
                                                    ? msgId.includes('notEnabled')
                                                        ? 'error'
                                                        : 'success'
                                                    : 'error',
                                                message: msgId ? intl.formatMessage({ id: msgId }) : r.message,
                                            });
                                        })
                                        .catch((err) => {
                                            Sentry.captureException(err);
                                            onMessage({
                                                type: 'error',
                                                message: intl.formatMessage({ id: 'AccountEditing.error.reset2FA' }),
                                            });
                                        });
                                }}
                            >
                                <Icon icon="shield-off" size="16px" />{' '}
                                <FormattedMessage id="AccountEditing.btn.reset2FA" />
                            </div>

                            <div className={classes.Divider} />

                            <div className={classes.Block}>
                                <div className={classes.Col_1}>
                                    <h4 className={classes.BlockTitle}>
                                        <FormattedMessage id="AccountEditing.title.contacts" />
                                    </h4>
                                </div>
                                <div className={classes.Col_2}>
                                    <InputHelper
                                        value={form.firstName}
                                        fieldName="firstName"
                                        label="FieldsHelper.label.firstName"
                                        isDisabled={true}
                                    />
                                    <InputHelper
                                        value={form.patronymic}
                                        fieldName="patronymic"
                                        label="FieldsHelper.label.patronymic"
                                        isDisabled={true}
                                    />
                                </div>
                                <div className={classes.Col_3}>
                                    <InputHelper
                                        value={form.lastName}
                                        fieldName="lastName"
                                        label="FieldsHelper.label.lastName"
                                        isDisabled={true}
                                    />
                                    <InputHelper
                                        value={form.email}
                                        fieldName="email"
                                        label="FieldsHelper.label.email"
                                        isDisabled={true}
                                    />
                                </div>
                            </div>

                            <div className={classes.Divider} />

                            <div className={classes.Block}>
                                <div className={classes.Col_1}>
                                    <h4 className={classes.BlockTitle}>
                                        <FormattedMessage id="AccountEditing.title.merchants" />
                                    </h4>
                                </div>
                                <div className={classes.ColFullWidth}>
                                    <ProjectSearchList
                                        token={token}
                                        projectsList={projectsList}
                                        projects={form.projects}
                                        onProjectsChange={(projects) => setForm({ ...form, projects })}
                                        onEditPermissions={onEditPermissions}
                                        accountId={data.id}
                                        globalPermissionsEnabled={globalPermissionsEnabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={classes.CardFooter}>
                            <button className={classes.BtnPrimary} onClick={onSaveAccountData} disabled={isLoading}>
                                <FormattedMessage
                                    id={isLoading ? 'AccountEditing.btn.saving' : 'AccountEditing.btn.save'}
                                />
                            </button>
                        </div>
                    </Fragment>
                ) : (
                    <div className={classes.Content}>
                        <div className={classes.Empty}>
                            <h5>
                                <FormattedMessage id="AccountEditing.empty" />
                            </h5>
                        </div>
                    </div>
                )}
            </div>

            {permissionsModal.open && permissionsModal.project && (
                <div
                    className={classes.ModalOverlay}
                    onClick={() => setPermissionsModal({ open: false, project: null, accountId: null })}
                >
                    <div className={classes.PermissionsModal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.ModalHeader}>
                            <h4>
                                {globalPermissionsEnabled ? (
                                    <span>
                                        <FormattedMessage
                                            id="AccountEditing.modal.permissionsGlobal"
                                            values={{
                                                project:
                                                    permissionsModal.project.name ||
                                                    permissionsModal.project.displayName ||
                                                    '',
                                            }}
                                        />
                                    </span>
                                ) : (
                                    <FormattedMessage
                                        id="AccountEditing.modal.permissions"
                                        values={{
                                            project:
                                                permissionsModal.project.name ||
                                                permissionsModal.project.displayName ||
                                                '',
                                        }}
                                    />
                                )}
                            </h4>
                            <button
                                className={classes.ModalClose}
                                onClick={() => setPermissionsModal({ open: false, project: null, accountId: null })}
                            >
                                ×
                            </button>
                        </div>
                        <div className={classes.ModalBody}>
                            {globalPermissionsEnabled ? (
                                <PermissionsEditor
                                    token={token}
                                    accountId={permissionsModal.accountId}
                                    isGlobal={true}
                                    readOnly={true}
                                    readOnlyMessage={intl.formatMessage({
                                        id: 'AccountEditing.global.readOnlyMessage',
                                    })}
                                    onClose={() => setPermissionsModal({ open: false, project: null, accountId: null })}
                                />
                            ) : (
                                <PermissionsEditor
                                    token={token}
                                    accountId={permissionsModal.accountId}
                                    merchantUuid={permissionsModal.project.uuid}
                                    onClose={() => setPermissionsModal({ open: false, project: null, accountId: null })}
                                    onSaved={() => {
                                        setPermissionsModal({ open: false, project: null, accountId: null });
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {globalPermissionsModal && (
                <div className={classes.ModalOverlay} onClick={() => setGlobalPermissionsModal(false)}>
                    <div className={classes.PermissionsModal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.ModalHeader}>
                            <h4>
                                <FormattedMessage id="AccountEditing.global.modalTitle" />
                            </h4>
                            <button className={classes.ModalClose} onClick={() => setGlobalPermissionsModal(false)}>
                                ×
                            </button>
                        </div>
                        <div className={classes.ModalBody}>
                            <PermissionsEditor
                                token={token}
                                accountId={data.id}
                                isGlobal={true}
                                onClose={() => setGlobalPermissionsModal(false)}
                                onSaved={(newPrivileges) => {
                                    setGlobalPrivileges(newPrivileges);
                                    setGlobalPermissionsModal(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default AccountEditing;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/AccountEditing/index.js